import React, { FC } from 'react';
import { createFeature, useTranslation } from '@edreamsodigeo/frontend-core';
import {
  Col,
  Flex,
  Glyph,
  Pill,
  PillTheme,
  PillType,
} from '@edreamsodigeo/prisma-design-system';
import { getItineraryDuration } from '../../../../../clientState/providers/ParsedSearchResponseProvider';
import { PriceFreezePromo } from '@components/Results/Components/Itinerary/PersuasiveMessages/PersuasiveMessages';
import { useItinerary } from '@components/Results/Components/Itinerary/Provider';
import {
  PRICE_FREEZE_MOBILE,
  PERSONALISED_DECORATION,
  RAILS_EARLIEST_SORTING,
} from 'clientState/ab/aliases';
import { Money } from '../../../../../generated/graphql';
import { useTrainSearch } from 'clientState/providers/SearchRequestProvider';

interface PersuasivePillProps {
  type: PillType;
  theme?: PillTheme;
  rounded?: boolean;
  isUnavailableItinerary?: boolean;
}
export interface PillsProps {
  isUnavailableItinerary?: boolean;
}

export const PersuasivePill: FC<PersuasivePillProps> = ({
  isUnavailableItinerary,
  type,
  theme,
  rounded,
  children,
}) => {
  return (
    <Pill
      type={type}
      theme={theme}
      rounded={rounded}
      sx={
        isUnavailableItinerary
          ? { border: '1px solid', borderColor: 'neutral.60' }
          : {}
      }
    >
      {children}
    </Pill>
  );
};

const GeniusPillComponent: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const { t } = useTranslation();
  return (
    <PersuasivePill
      type="positive"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      {t('results.highlighted.itinerary.recommended')}
    </PersuasivePill>
  );
};
export const GeniusPill = createFeature({
  name: PERSONALISED_DECORATION,
  variations: [GeniusPillComponent, null],
  version: 1,
});

export const CheapestPill: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const { t } = useTranslation();
  return (
    <PersuasivePill
      type="positive"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      {t('results.highlighted.itinerary.cheapest')}
    </PersuasivePill>
  );
};

export const HotelVoucher: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const { t } = useTranslation();
  return (
    <PersuasivePill
      type="promotional"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      {t('voucher.pill.text')}
    </PersuasivePill>
  );
};

export const FastestPill: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const { t } = useTranslation();
  return (
    <PersuasivePill
      type="informative"
      theme="soft"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      {t('results.highlighted.itinerary.fastest')}
    </PersuasivePill>
  );
};
const EarliestPillBase: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const { t } = useTranslation();
  return (
    <PersuasivePill
      type="informative"
      theme="soft"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      {t('results.highlighted.itinerary.earliest')}
    </PersuasivePill>
  );
};

const EarliestPillVersioned = createFeature({
  name: RAILS_EARLIEST_SORTING,
  variations: [null, EarliestPillBase],
  version: 1,
});

export const EarliestPill: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const isTrainSearch = useTrainSearch();
  return isTrainSearch ? (
    <EarliestPillVersioned isUnavailableItinerary={isUnavailableItinerary} />
  ) : (
    <></>
  );
};

type PriceFreezePillProps = {
  isPriceFreezeAvailable: boolean | null;
  loading: boolean;
  deposit: Money | null;
  isPrimeUser: boolean;
  isUnavailableItinerary?: boolean;
};

const PriceFreezePill: FC<PriceFreezePillProps> = ({
  isPriceFreezeAvailable,
  loading,
  deposit,
  isPrimeUser,
  isUnavailableItinerary,
}) => {
  return (
    <PriceFreezePromo
      available={isPriceFreezeAvailable}
      loading={loading}
      deposit={deposit}
      isPrimeUser={isPrimeUser}
      returnFormattedText={loading}
      shortMessage={true}
      isUnavailableItinerary={isUnavailableItinerary}
    />
  );
};

export const PriceFreezeVersionedComponentTag = createFeature({
  name: PRICE_FREEZE_MOBILE,
  variations: [null, PriceFreezePill, PriceFreezePill, PriceFreezePill],
  version: 1,
});

export const NightsPill: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const { t } = useTranslation();
  const { legs } = useItinerary();
  const numNights = getItineraryDuration(legs);
  const context = numNights === 1 ? 'some' : 'some_plural';
  return (
    <PersuasivePill
      type="informative"
      theme="soft"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      {t('semresults.filters.nights.label', {
        count: numNights,
        context,
      })}
    </PersuasivePill>
  );
};

export const CarbonFootprintPill: FC<PillsProps> = ({
  isUnavailableItinerary,
}) => {
  const { t } = useTranslation();
  const { carbonFootprint } = useItinerary();
  const { ecoPercentageThanAverage } = carbonFootprint || {};
  const text =
    ecoPercentageThanAverage === -1
      ? t('carbonFootprint.card.greenestshort')
      : t('carbonFootprint.card.abbreviated', {
          percentage: ecoPercentageThanAverage,
        });

  return (
    <PersuasivePill
      type="positive"
      theme="soft"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      <Flex flexDirection="row" alignItems="center">
        <Col>
          <Glyph
            category="Deals"
            name="Leaf"
            color="positive.default"
            size="tiny"
          />
        </Col>
        {text}
      </Flex>
    </PersuasivePill>
  );
};

export const PrimeDayPill: FC<PillsProps> = ({ isUnavailableItinerary }) => {
  const { t } = useTranslation();
  return (
    <PersuasivePill
      type="promotional"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      {t('results.primeday.card.pill')}
    </PersuasivePill>
  );
};
